/**
 * errorCapture — lightweight last-API-error store for the support widget.
 *
 * Stores the most recent failed API call in window.__lastPortalError so the
 * support widget can include it as context when a user files a ticket.
 *
 * To disable at runtime (e.g. if performance impact is observed):
 *   localStorage.setItem('bc-portal-error-capture', 'false')
 * To re-enable:
 *   localStorage.removeItem('bc-portal-error-capture')
 */

export interface CapturedApiError {
  timestamp: string;
  url: string;
  method: string;
  status: number | null;   // null for network errors
  requestBody: unknown;
  responseBody: unknown;
  errorMessage: string;
}

type ErrorWindow = Window & { __lastPortalError?: CapturedApiError };

function isEnabled(): boolean {
  try {
    return localStorage.getItem('bc-portal-error-capture') !== 'false';
  } catch {
    return true;
  }
}

export function captureApiError(
  url: string,
  method: string,
  status: number | null,
  requestBody: unknown,
  responseBody: unknown,
  errorMessage: string
): void {
  if (!isEnabled()) return;
  try {
    (window as ErrorWindow).__lastPortalError = {
      timestamp: new Date().toISOString(),
      url,
      method: method.toUpperCase(),
      status,
      requestBody,
      responseBody,
      errorMessage,
    };
  } catch { /* ignore */ }
}

export function getLastPortalError(): CapturedApiError | undefined {
  return (window as ErrorWindow).__lastPortalError;
}

export function clearLastPortalError(): void {
  delete (window as ErrorWindow).__lastPortalError;
}
